import type { ReactNode } from "react";

import type {
  AgenticLogicAttemptRecord,
  AgenticLogicOnEmpty,
  AgenticLogicSurface,
} from "@intelligems/ig-types";
import type { ChipProps } from "@mui/material";

import type { RuleGraphScenarioContext } from "./scenario-context";

/**
 * Per-node overlay data a cart-scenario evaluation fills (branchFired
 * telemetry, preview trace). With no scenario selected the card renders
 * overlay-free. The object rides into the flow node's `data`, so overlay
 * changes touch only the card's render and their own data fetch — never
 * layout or wiring.
 */
export interface RuleGraphNodeOverlay {
  /** Scenario-specific replacement for the step card's static body. */
  bodyText?: string;
  /** e.g. "matched · 3 products" on the fired branch node. */
  chip?: { label: string; color: ChipProps["color"] };
  /** Preview trace: node not traversed by the sample evaluation. */
  dimmed?: boolean;
  /**
   * The run walk's current beat lands on this node right now — plays the
   * one-shot settle animation. Only ever set by the walk's time-slicing;
   * the static overlays (and therefore the walk's final frame) never carry
   * it.
   */
  active?: boolean;
  /** Extra section rendered in the details panel for this node. */
  detail?: ReactNode;
  /**
   * "What the shopper sees" — the evaluated products in envelope order plus
   * a one-line branch-level explanation, or the empty face when the cart
   * fell through. It rides on the node the evaluation ended on: the OUTPUT
   * node for the products face, the FALLBACK TERMINAL for the empty face
   * (its card slot renders the panel in place of "Hide the upsell").
   */
  result?: {
    products: RuleGraphOutputProduct[];
    explanation: string;
    empty: boolean;
    /**
     * What an empty envelope renders as — the rule's onEmpty policy. Drives
     * the empty face's copy: "hide" hides the slot, "smart-recs" fills it
     * with smart recommendations.
     */
    onEmpty: AgenticLogicOnEmpty;
    /**
     * The request the run was judged on (surface, delivery, orders placed),
     * so the panel can say WHICH checkout this answer is for. Absent when
     * the run's input did not parse.
     */
    scenarioContext?: RuleGraphScenarioContext;
  };
  /** Raw attempt records for the details panel to render. */
  attempts?: AgenticLogicAttemptRecord[];
  /**
   * The cart the run started from. Rides ONLY on the synthetic cart-input
   * node ({@link RULE_GRAPH_CART_INPUT_NODE_ID}), which the canvas draws at
   * the head of the rows while a run is on the graph.
   */
  cartInput?: RuleGraphCartInput;
}

/**
 * The flow-node id of the cart-input panel. Not a DTO node: the graph
 * artifact has an `input` node of its own that heads a row in some lane
 * shapes, so the run's cart is a node the RENDERER adds, keyed here so the
 * overlay builder, the run walk and the flow-node projection agree on it.
 */
export const RULE_GRAPH_CART_INPUT_NODE_ID = "__cart-input__";

export interface RuleGraphCartInputLine {
  /** The most specific GID the line carries: its variant, else its product. */
  id: string;
  title: string;
  /** The variant's own title, when the line resolved to one; null otherwise. */
  variantTitle: string | null;
  quantity: number;
  /** Pre-formatted with currency ("$24.00"). */
  unitPrice: string;
  imageUrl: string | null;
}

/**
 * "Cart" — what the run put in: the lines, the totals, and the request
 * context the rule actually conditions on. The optional captions exist only
 * when the graph has a branch keyed on that context; a rule that never reads
 * the shipping gap says nothing about it.
 */
export interface RuleGraphCartInput {
  lines: RuleGraphCartInputLine[];
  itemCount: number;
  /** Pre-formatted with currency ("$50.00"). */
  subtotal: string;
  surface: AgenticLogicSurface;
  /** Free-shipping standing, when a branch conditions on `shipping`. */
  shipping?: string;
  /** The checkout delivery selection, when a branch conditions on `delivery`. */
  delivery?: string;
  /** Who is buying, when a branch conditions on `customer`. */
  customer?: string;
}

export interface RuleGraphOutputProduct {
  /**
   * The envelope item's own id, at its own grain: a Product GID for a product
   * item, a ProductVariant GID for a variant item.
   */
  id: string;
  title: string;
  /**
   * What distinguishes two rows of the same product ("Ice", "Dawn"); null for
   * a product-grain row.
   */
  variantTitle?: string | null;
  /** Pre-formatted with currency by the backend ("$24.00"). */
  price: string;
  /**
   * Resolved from the item's variant/product GID over the admin's own
   * Shopify GraphQL session — the evaluation response carries ids, never
   * pictures.
   */
  imageUrl: string | null;
}

export type RuleGraphOverlaysByNodeId = Record<string, RuleGraphNodeOverlay>;

/**
 * Edge overlays exist for exactly one reason: dimming only nodes leaves
 * solid amber fallback edges cutting through a ghosted lane. Keyed by
 * `edgeOverlayId` (the same id the flow edges carry).
 */
export interface RuleGraphEdgeOverlay {
  dimmed?: boolean;
  /** The run walk's rail beat is filling this edge right now. */
  active?: boolean;
}

export type RuleGraphOverlaysByEdgeId = Record<string, RuleGraphEdgeOverlay>;

/** DTO edges carry no id of their own; this is the shared derivation. */
export const edgeOverlayId = (edge: {
  from: string;
  to: string;
  kind: string;
}): string => `${edge.from}->${edge.to}:${edge.kind}`;
